import type {
  FilterItem,
  FindManyFilterGroupItem,
  FindManyFilterItemV2,
  FindManyFilterV2,
} from '@rialtic/types'
import { FilterType } from '@rialtic/types'
import {
  UiFilterDatePicker,
  UiFilterDropDownGroup,
  UiFilterDropDownNew,
  UiFilterInput,
  UiFilterMultiSelectInput,
} from '#components'
import { computed, getCurrentInstance } from '#imports'
import { nonFilterableQueries } from '../constants'
import { useUrlParamsState } from './urlParamsState'

export type FilterItemWithState<T = FilterItem> = T & {
  active?: boolean
  disabled?: boolean
  selected?: boolean
  // TODO: this type is mapped only for the available filter selector(s) - added here to simplify type fixes
  group?: string
}

const filterComponents = {
  [FilterType.GROUP]: UiFilterDropDownGroup,
  [FilterType.MULTIPLE]: UiFilterDropDownNew,
  [FilterType.SINGLE]: UiFilterDropDownNew,
  [FilterType.DATE_RANGE]: UiFilterDatePicker,
  [FilterType.INPUT]: UiFilterInput,
  [FilterType.MULTISELECT_INPUT]: UiFilterMultiSelectInput,
}

const selectAllFiltersList = ['exclusion_ids']

export function useFiltersState(ignoredFilters: string[] = []) {
  const { normalizedParams, getUrlParams, setUrlParams, deleteUrlParam } =
    useUrlParamsState()

  const { emit } = getCurrentInstance()!

  // TODO: we need to consider what an active filter is vs a non-filter based URL param
  const hasActiveFilters = computed<boolean>(
    () => Object.keys(processableFilters.value).length > 0,
  )

  const processableFilters = computed<Record<string, string | string[]>>(() => {
    const urlParams = normalizedParams.value
    const paramKeys = Object.keys(urlParams)

    return urlParams && paramKeys.length
      ? paramKeys.reduce(
          (accumulator, currentValue) => {
            if (
              [...nonFilterableQueries, ...ignoredFilters].includes(
                currentValue,
              )
            )
              return accumulator

            accumulator[currentValue] = urlParams[currentValue] as
              | string
              | string[]
            return accumulator
          },
          {} as Record<string, string | string[]>,
        )
      : {}
  })

  const buildExportedFilters = <T extends FindManyFilterV2 = FindManyFilterV2>(
    filters?: T[] | null,
  ): { name: string; filters: string | string[] }[] => {
    if (!processableFilters.value || !filters?.length) return []
    const filterKeys = Object.entries(processableFilters.value)

    return filterKeys.map(([filterKey, filterValues]) => {
      const filter = filters.find((f) => f.name === filterKey)
      return {
        name: filter?.label || filterKey,
        filters:
          (filter?.items || [])
            .filter((item) =>
              processableFilters.value[filterKey]!.includes(item.value),
            )
            .map((item) => item.label || item.value) ?? filterValues,
      }
    })
  }

  const clearAllFilters = (paramName: string) => {
    emit('filter-clear', paramName)

    deleteUrlParam(paramName)
  }

  const removeUrlParamFilter = (paramName: string, paramValue: string) => {
    const params = getUrlParams(paramName)
    const revisedParams = params.filter((param) => param !== paramValue)

    if (!revisedParams.length) {
      deleteUrlParam(paramName)
    } else {
      setUrlParams(paramName, revisedParams)
    }
  }

  const getAvailableFilters = <T extends FindManyFilterV2 = FindManyFilterV2>(
    filters: T[],
    defaults: string[] = [],
  ) => {
    const filterKeys = Object.keys(normalizedParams.value)

    return {
      isMultiple: true,
      items: filters.map((filterItem) => {
        if (filterItem.name === 'exclusion_state') {
          const anyExclusionFilter = filterItem.items!.find(
            ({ value }) => value === 'excluded',
          ) as FindManyFilterGroupItem

          anyExclusionFilter!.childFilter!.items =
            anyExclusionFilter!.childFilter!.items!.map((childItem) => ({
              ...childItem,
              selected: true,
              disabled: true,
            }))
        }

        return {
          active:
            defaults.includes(filterItem.name) ||
            filterKeys.includes(filterItem.name),
          label: filterItem.label,
          value: filterItem.name,
          group: (filterItem as FindManyFilterV2).group,
        }
      }),
      label: 'Filters',
      name: 'Filters',
    }
  }

  const filterChange = <T extends FindManyFilterItemV2 = FindManyFilterItemV2>(
    item: FilterItemWithState<T>,
    name: string,
    isMultiple = true,
    isChildItem = false,
  ) => {
    emit('filter-change', { item, name })

    if (isChildItem) {
      item.selected = !item.selected
    }

    if (item.active || (isChildItem && !item.selected)) {
      removeUrlParamFilter(name, item.value)
    } else {
      const params: string | string[] = !isMultiple
        ? item.value.toString()
        : [...getUrlParams(name), item.value]
      setUrlParams(name, params)
    }
  }

  const selectAllFilters = (paramName: string, items: FilterItem[]) => {
    emit('filter-all', paramName)

    setUrlParams(paramName, items.map((item) => item.value).join('|'))
  }

  return {
    buildExportedFilters,
    clearAllFilters,
    filterChange,
    filterComponents,
    getAvailableFilters,
    hasActiveFilters,
    processableFilters,
    selectAllFilters,
    selectAllFiltersList,
  }
}
